import React, { useRef } from 'react';

/**
 * The EVM Galaxy: A Visual Journey Through Smart Contract Storage
 * Full article component converted from evm_galaxy_article.html
 */
const EVMGalaxy = () => {
  const viz1Ref = useRef(null);
  const viz2Ref = useRef(null);
  const viz3Ref = useRef(null);
  const viz4Ref = useRef(null);

  const reloadViz = (ref) => {
    if (ref.current) {
      const src = ref.current.src;
      ref.current.src = src;
    }
  };

  const openFullscreen = (ref) => {
    if (ref.current) {
      if (ref.current.requestFullscreen) {
        ref.current.requestFullscreen();
      } else if (ref.current.webkitRequestFullscreen) {
        ref.current.webkitRequestFullscreen();
      } else if (ref.current.msRequestFullscreen) {
        ref.current.msRequestFullscreen();
      }
    }
  };

  const styles = {
    // Outer wrapper: matches HTML body — full-width gradient, no max-width
    wrapper: {
      fontFamily: "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)',
      color: '#e0e0e0',
      lineHeight: 1.7,
      minHeight: '100vh',
      padding: 0,
    },
    // Inner container: matches HTML .container — content width and padding
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '60px 30px',
    },
    h1: {
      fontSize: '2.8rem',
      fontWeight: 700,
      color: '#fff',
      marginBottom: '30px',
      textShadow: '0 0 30px rgba(139, 92, 246, 0.3)',
      lineHeight: 1.2,
      textAlign: 'center',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#a5b4fc',
      marginTop: '60px',
      marginBottom: '25px',
      borderBottom: '2px solid rgba(139, 92, 246, 0.3)',
      paddingBottom: '10px',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#c4b5fd',
      marginTop: '40px',
      marginBottom: '15px',
    },
    p: {
      marginBottom: '20px',
      color: '#d1d5db',
      fontSize: '1.05rem',
    },
    visualization: {
      margin: '50px 0',
      background: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '16px',
      padding: '30px',
      boxShadow: '0 0 50px rgba(139, 92, 246, 0.15), inset 0 0 50px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(139, 92, 246, 0.2)',
    },
    vizHeader: {
      textAlign: 'center',
      marginBottom: '25px',
    },
    vizTitle: {
      fontSize: '1.3rem',
      fontWeight: 600,
      color: '#a78bfa',
      marginBottom: '8px',
    },
    vizDescription: {
      fontSize: '0.95rem',
      color: '#9ca3af',
      fontStyle: 'italic',
    },
    vizFrame: {
      width: '100%',
      border: 'none',
      borderRadius: '12px',
      background: '#000',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
      display: 'block',
      height: '750px',
    },
    vizFrameTall: {
      width: '100%',
      border: 'none',
      borderRadius: '12px',
      background: '#000',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
      display: 'block',
      height: '920px',
    },
    vizControls: {
      display: 'flex',
      justifyContent: 'center',
      gap: '15px',
      marginTop: '20px',
    },
    vizBtn: {
      background: 'rgba(139, 92, 246, 0.2)',
      border: '1px solid rgba(139, 92, 246, 0.4)',
      color: '#a78bfa',
      padding: '10px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontFamily: "'Space Grotesk', sans-serif",
      fontSize: '0.9rem',
      fontWeight: 500,
      transition: 'all 0.3s ease',
    },
    code: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#a78bfa',
      fontSize: '0.95em',
    },
    codeUint: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#4ec9b0',
      fontSize: '0.95em',
    },
    codeKeccak: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#f59e0b',
      fontSize: '0.95em',
    },
    codePurple: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#a78bfa',
      fontSize: '0.95em',
    },
    codeVar: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#9cdcfe',
      fontSize: '0.95em',
    },
    codeString: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#ce9178',
      fontSize: '0.95em',
    },
    codeNum: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#b5cea8',
      fontSize: '0.95em',
    },
    codeOp: {
      fontFamily: "'Fira Code', 'Courier New', monospace",
      background: 'rgba(139, 92, 246, 0.1)',
      padding: '2px 8px',
      borderRadius: '4px',
      color: '#c586c0',
      fontSize: '0.95em',
    },
    pre: {
      background: 'rgba(0, 0, 0, 0.4)',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      borderRadius: '8px',
      padding: '20px',
      overflowX: 'auto',
      margin: '25px 0',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
    preCode: {
      background: 'none',
      padding: 0,
      color: '#e0e0e0',
      fontSize: '0.9rem',
      fontFamily: "'Fira Code', 'Courier New', monospace",
    },
    ul: {
      listStyleType: 'disc',
      paddingLeft: '24px',
      margin: '16px 0',
      display: 'block',
    },
    li: {
      display: 'list-item',
      fontSize: '1.05rem',
      lineHeight: '2',
      color: '#e2e8f0',
      verticalAlign: 'middle',
      marginBottom: '4px',
    },
    // Simple inline code for namespace slots list
    codeSimple: {
      fontSize: '1.05rem',
      fontFamily: 'monospace',
      verticalAlign: 'middle',
      color: '#a78bfa',
    },
    strong: {
      color: '#fff',
      fontWeight: 600,
    },
    hr: {
      border: 'none',
      height: '2px',
      background: 'linear-gradient(to right, transparent, rgba(139, 92, 246, 0.5), transparent)',
      margin: '50px 0',
    },
    whyDoubleHash: {
      background: 'rgba(30, 41, 59, 0.8)',
      borderRadius: '12px',
      padding: '25px',
      margin: '30px 0',
      borderLeft: '4px solid #8b5cf6',
    },
    whyDoubleHashH3: {
      color: '#a78bfa',
      marginTop: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    tableStyle: {
      width: '100%',
      marginTop: '20px',
      borderCollapse: 'collapse',
      fontSize: '0.8rem',
    },
    thStyle: {
      textAlign: 'left',
      padding: '10px',
      borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
    },
    tdStyle: {
      padding: '10px',
      borderBottom: '1px solid rgba(139, 92, 246, 0.15)',
    },
    stepItem: {
      marginBottom: '14px',
      fontSize: '1.05rem',
      color: '#d1d5db',
    },
    stepDesc: {
      fontSize: '1.05rem',
    },
    // Metadata header styles
    metaHeader: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      marginBottom: '40px',
      paddingBottom: '20px',
      borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '0.9rem',
      color: '#9ca3af',
    },
    metaIcon: {
      fontSize: '1rem',
    },
    metaCategory: {
      background: 'rgba(139, 92, 246, 0.2)',
      color: '#a78bfa',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: 500,
    },
    metaTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      justifyContent: 'center',
      marginTop: '8px',
      width: '100%',
    },
    metaTag: {
      background: 'rgba(139, 92, 246, 0.1)',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      color: '#c4b5fd',
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '0.8rem',
    },
  };

  return (
    <div className="evm-galaxy-article" style={styles.wrapper}>
      <div className="evm-galaxy-article__container" style={styles.container}>
      <h1 style={styles.h1}>The EVM Galaxy: A Visual Journey Through Smart Contract Storage</h1>

      {/* Article Metadata */}
      <div style={styles.metaHeader}>
        <span style={styles.metaItem}>
          <span style={styles.metaIcon}>📅</span> Feb 26, 2026
        </span>
        <span style={styles.metaCategory}>Blockchain</span>
        <span style={styles.metaItem}>
          <span style={styles.metaIcon}>⏱️</span> 15 min read
        </span>
        <div style={styles.metaTags}>
          {['Ethereum', 'Solidity', 'EVM', 'EIP-7201', 'Storage'].map(tag => (
            <span key={tag} style={styles.metaTag}>{tag}</span>
          ))}
        </div>
      </div>

      {/* Introduction */}
      <section id="intro">
        <p style={styles.p}>
          I won't bore you with complex definitions. Instead, let me take you on a journey of minds and pictures, 
          a way of seeing Ethereum's storage that transformed my understanding of the Virtual Machine itself.
        </p>
        <p style={styles.p}>
          You've probably heard this before: "The Ethereum Virtual Machine (EVM) is the runtime environment for 
          every smart contract on Ethereum." True. But definitions only take us so far. What if you could <em>see</em> 
          the EVM? Picture a complex, layered universe where data lives, moves, and interacts. That's the mental 
          model we're about to build.
        </p>
        <p style={styles.p}>
          Imagine the EVM as a vast, empty dark galaxy. The deployment of your smart contract is like the birth 
          of a new world within this galaxy, landing at a specific location. If you've seen the movie Battleship, 
          observe how the shield was used to secure a perimeter around them. Your contract does something 
          similar. Upon deployment, it claims a unique address in the EVM universe and is surrounded by its own 
          vast expanse of storage space — 2²⁵⁶ slots in size. This storage space is exclusively yours, a secure 
          territory where your contract's data lives, organized in ways that are both fascinating and complex.
        </p>
      </section>

      {/* Visualization 1: Contract Arrival */}
      <section id="viz1">
        <div style={styles.visualization}>
          <div style={styles.vizHeader}>
            <div style={styles.vizTitle}>🌌 Visualization 1: Contract Arrival</div>
            <div style={styles.vizDescription}>Watch a smart contract deploy into the EVM storage space</div>
          </div>
          <iframe
            ref={viz1Ref}
            className="evm-viz-frame"
            style={styles.vizFrame}
            src="/visualizations/viz_1_the_evm_galaxy_contract_arrival.html"
            title="Contract Arrival Visualization"
          />
          <div style={styles.vizControls}>
            <button style={styles.vizBtn} onClick={() => reloadViz(viz1Ref)}>↻ Replay Animation</button>
            <button style={styles.vizBtn} onClick={() => openFullscreen(viz1Ref)}>⛶ Fullscreen</button>
          </div>
        </div>
      </section>

      <p style={styles.p}>
        In this first visualization, we see a smart contract being deployed into the EVM galaxy. It lands at a 
        unique address, surrounded by its own vast expanse of storage space. This storage space is where your 
        contract's data lives. The EVM manages it using slots, each capable of holding a 32-byte value. But 
        here's the fascinating part: Solidity organizes this space in two distinct patterns.
      </p>

      {/* Visualization 2: Storage Layout */}
      <section id="viz2">
        <h2 style={styles.h2}>Understanding Storage Layout</h2>
        
        <p style={styles.p}>
          Now that we've seen how a contract arrives in the EVM galaxy, let's explore how its storage is organized.
        </p>
        <p style={styles.p}>Let's ground this with a concrete example:</p>
        
        <pre style={styles.pre}>
          <code style={styles.preCode}>
            <span style={{ color: '#c586c0' }}>contract</span> <span style={{ color: '#4ec9b0' }}>StorageExample</span> {'{'}
            {'\n'}    <span style={{ color: '#4ec9b0' }}>uint256</span> <span style={{ color: '#569cd6' }}>public</span> data1;       <span style={{ color: '#6a9955' }}>// Stored in Slot 0</span>
            {'\n'}    <span style={{ color: '#4ec9b0' }}>uint256</span> <span style={{ color: '#569cd6' }}>public</span> data2;       <span style={{ color: '#6a9955' }}>// Stored in Slot 1</span>
            {'\n'}    <span style={{ color: '#4ec9b0' }}>uint256[]</span> <span style={{ color: '#569cd6' }}>public</span> dataArray; <span style={{ color: '#6a9955' }}>// Starts at Slot 2 (array length) and elements at keccak256(2) + index</span>
            {'\n'}{'}'}
          </code>
        </pre>

        <h3 style={styles.h3}>Sequential Storage (→ Right)</h3>
        <p style={styles.p}>
          Variables like <code style={styles.codeUint}>uint256</code>, <code style={styles.codeUint}>uint256</code>, 
          and <code style={styles.codeUint}>uint256[]</code> are stored sequentially. Slot 0, then Slot 1, then 
          Slot 2, marching in a straight line from the origin point, extending to the right. These are like 
          planets arranged in a neat row, each one predictably positioned next to the last.
        </p>

        <h3 style={styles.h3}>Hashed Storage (↑ Upward)</h3>
        <p style={styles.p}>
          Dynamic data structures like arrays and mappings behave differently. They can't be stored sequentially 
          because their size isn't known at compile time. Instead, Solidity uses <code style={styles.codeKeccak}>keccak256</code> 
          hashing to calculate a location far away from the sequential slots.
        </p>
        <p style={styles.p}>
          For an array stored at Slot 2, its elements don't live at Slots 3, 4, 5... Instead, they leap to a 
          location calculated by <code style={styles.codeKeccak}>keccak256(2)</code> like a portal at Slot 2 
          that teleports you to a distant region of the galaxy. From there, elements grow sequentially: 
          <code style={styles.codeKeccak}>keccak256(2) + 0</code> for the first element, 
          <code style={styles.codeKeccak}>keccak256(2) + 1</code> for the second, and so on. The sequential 
          slots remain uncluttered, and the array has room to breathe. The location is a random 32-byte number 
          that is entirely deterministic and reproducible. Push another element, and it lands at 
          <code style={styles.codeKeccak}>keccak256(2) + 1</code>. The array grows outward from its hashed 
          starting point, leaving the sequential slots (3, 4, 5...) available for other variables you might add later.
        </p>
        <p style={styles.p}>
          This is the elegant dance of EVM storage: predictable sequential allocation for fixed-size data, 
          and hashed allocation for dynamic data that needs room to breathe.
        </p>

        <div style={styles.visualization}>
          <div style={styles.vizHeader}>
            <div style={styles.vizTitle}>🗂️ Visualization 2: Contract Storage Layout</div>
            <div style={styles.vizDescription}>Exploring how Solidity organizes storage slots</div>
          </div>
          <iframe
            ref={viz2Ref}
            className="evm-viz-frame"
            style={styles.vizFrame}
            src="/visualizations/viz_2_the_evm_galaxy_contract_storage_layout.html"
            title="Storage Layout Visualization"
          />
          <div style={styles.vizControls}>
            <button style={styles.vizBtn} onClick={() => reloadViz(viz2Ref)}>↻ Replay Animation</button>
            <button style={styles.vizBtn} onClick={() => openFullscreen(viz2Ref)}>⛶ Fullscreen</button>
          </div>
        </div>
      </section>

      {/* Visualization 3: EIP-7201 */}
      <section id="viz3">
        <h2 style={styles.h2}>The Collision Problem</h2>
        <p style={styles.p}>
          Both patterns grow in the same general direction what we might call "right and upward" in our galaxy 
          metaphor. As long as you're working with a single contract and following Solidity's rules, everything 
          stays organized.
        </p>
        <p style={styles.p}>
          But what happens when you introduce proxy contracts, upgradeable patterns, or inherited storage? 
          Suddenly, multiple contracts might try to claim the same slots. It's like two civilizations 
          independently deciding to colonize the same region of space, collision is inevitable. We both need 
          the Green land.
        </p>
        <p style={styles.p}>
          Before EIP-7201, developers had to carefully track storage layouts across contract versions, praying 
          that their slot calculations wouldn't collide with inherited contracts or future upgrades. It was 
          like trying to coordinate city planning across multiple civilizations in our galaxy without a map, 
          making it difficult for blockchain explorers and development tools like Hardhat or Foundry to visualize storage accurately.
        </p>
        <p style={styles.p}>
          Now, with namespaced storage, each <em>declared namespace</em> gets its own deterministic corner of 
          the universe. A proxy contract can store its admin variables in one namespace, while the implementation 
          contract declares a separate namespace for its business logic. Note: this is different from inheritance, 
          they still share the same storage layout unless they explicitly use separate namespaces. EIP-7201 gives 
          you the <em>tool</em> to separate storage; you still have to use it intentionally.
        </p>
        <p style={styles.p}>
          Observe the below to see the approach to storage that prevents collisions in upgradeable contracts 
          by using namespaced storage locations.
        </p>

        <div style={styles.visualization}>
          <div style={styles.vizHeader}>
            <div style={styles.vizTitle}>🎯 Visualization 3: EIP-7201 Storage Layout</div>
            <div style={styles.vizDescription}>Understanding namespaced storage and collision avoidance</div>
          </div>
          <iframe
            ref={viz3Ref}
            className="evm-viz-frame"
            style={styles.vizFrame}
            src="/visualizations/viz_3_the_evm_galaxy_eip_7201_storage_layout.html"
            title="EIP-7201 Visualization"
          />
          <div style={styles.vizControls}>
            <button style={styles.vizBtn} onClick={() => reloadViz(viz3Ref)}>↻ Replay Animation</button>
            <button style={styles.vizBtn} onClick={() => openFullscreen(viz3Ref)}>⛶ Fullscreen</button>
          </div>
        </div>
      </section>

      {/* Technical Details Section */}
      <section id="technical">
        <h2 style={styles.h2}>The Thoughts Behind EIP 7201</h2>
        <p style={styles.p}>
          <strong style={styles.strong}>The magic formula is: </strong>
          <code style={styles.codeKeccak}>keccak256</code>(<code style={styles.codeKeccak}>keccak256</code>(
          <code style={styles.codeVar}>id</code>) - 1) <code style={styles.codeOp}>&</code> <code style={styles.codeOp}>~</code>
          <code style={styles.codeNum}>0xff</code>
        </p>

        <p style={styles.p}>Let's break this down step by step, following BODMAS order:</p>

        <p style={styles.p}>
          <strong style={styles.strong}>Step 1: <code style={styles.codeKeccak}>keccak256(id)</code> Pick Your Coordinates:</strong>
        </p>
        <p style={styles.p}>
          When we calculate <code style={styles.codeKeccak}>keccak256(id)</code>, we're determining a unique 
          location in storage space for a given namespace identifier (like <code style={styles.codeString}>"erc7201:myproject.storage.v1"</code>). 
          This is the same mechanism Solidity uses to locate array elements remember how 
          <code style={styles.codeVar}>dataArray</code>'s elements live at <code style={styles.codeKeccak}>keccak256(2)</code>?
        </p>
        
        <pre style={styles.pre}>
          <code style={styles.preCode}>keccak256(id) = 0x29a...4b7</code>
        </pre>

        <p style={styles.p}>
          <strong style={styles.strong}>Step 2: <code style={styles.code}>- 1</code> Shift Direction:</strong>
        </p>
        <p style={styles.p}>
          Here's the clever bit. Solidity's arrays grow <em>forward</em>: <code style={styles.codeKeccak}>keccak256(slot) + 0</code>, 
          <code style={styles.codeKeccak}>keccak256(slot) + 1</code>, <code style={styles.codeKeccak}>keccak256(slot) + 2</code>...
          By subtracting 1, we intentionally shift our target location away from Solidity's natural growth pattern. 
          Instead of landing at <code style={styles.codeKeccak}>keccak256(id)</code>, we land at 
          <code style={styles.codeKeccak}>keccak256(id) - 1</code>, what this means is that, for a slot of 2, 
          the output that <code style={styles.codeKeccak}>keccak256(2) - 1</code> will generate will be different 
          from <code style={styles.codeKeccak}>keccak256(2) + 1</code>. This is a completely different point in 
          the galaxy, we're moving in the <em>opposite</em> direction "left or downward" in our galaxy metaphor 
          (Solidity will NEVER use <code style={styles.codeKeccak}>keccak256(id) - 1</code>). This ensures that 
          our namespace doesn't accidentally collide with any sequential storage slots or other hashed locations 
          that Solidity might use. This does not prevent collisions entirely, but a simple shift dramatically 
          reduces collision probability with any standard storage patterns. But wait, what if sequential storage 
          eventually reaches slot <code style={styles.codeNum}>2^256 - 1</code> and wraps around? (Astronomically 
          unlikely, but the EIP authors thought of everything.)
        </p>
        <p style={styles.p}>
          Beyond the above illustration, <code style={styles.code}>-1</code> serves a deeper cryptographic purpose. 
          Consider how Solidity handles nested dynamic structures:
        </p>
        <p style={styles.p}>For a mapping containing arrays at slot <code style={styles.codeVar}>m</code>:</p>
        <pre style={styles.pre}>
          <code style={styles.preCode}>
            <span style={{ color: '#9cdcfe' }}>mapping[key]</span> is at <span style={{ color: '#f59e0b' }}>keccak256(key ++ m)</span>{'\n'}If that value is an array, elements are at <span style={{ color: '#f59e0b' }}>keccak256(keccak256(key ++ m)) + index</span>
          </code>
        </pre>
        <p style={styles.p}>
          Notice the pattern: <code style={styles.codeKeccak}>keccak256(keccak256(something))</code>, the output 
          of one hash feeds directly into another.
        </p>
        <p style={styles.p}>
          If we used <code style={styles.codeKeccak}>keccak256(keccak256(id))</code> without the <code style={styles.code}>-1</code>, 
          our namespace input would be a raw keccak256 output. This means it <em>could</em> theoretically match 
          some intermediate hash that Solidity produces, like <code style={styles.codeKeccak}>keccak256(key ++ slot)</code> 
          for a nested mapping.
        </p>
        <p style={styles.p}>
          By subtracting 1, we ensure that <code style={styles.codeKeccak}>keccak256(id) - 1</code> is overwhelmingly 
          unlikely to be a valid keccak256 output itself. Why? Because finding a value X such that 
          <code style={styles.codeKeccak}>keccak256(X) = keccak256(id) - 1</code> would require breaking keccak256's 
          preimage resistance.
        </p>
        <p style={styles.p}>
          So the <code style={styles.code}>-1</code> isn't just a directional shift, it's a cryptographic "pattern 
          breaker" that ensures our input to the outer hash exists outside the space of values that Solidity's 
          storage system could ever naturally produce, but seeing it as a shift in our galaxy metaphor will help 
          to visualize the concept.
        </p>

        <pre style={styles.pre}>
          <code style={styles.preCode}>keccak256(id) - 1 = 0x67a...fx5</code>
        </pre>

        <p style={styles.p}>
          <strong style={styles.strong}>Step 3: <code style={styles.codeKeccak}>keccak256(...)</code> Rehash for Randomness:</strong>
        </p>
        <p style={styles.p}>
          After shifting (subtracting 1), we rehash the result with <code style={styles.codeKeccak}>keccak256</code> 
          again. This scatters our namespace randomly across the entire storage space. Two different namespace 
          identifiers will almost certainly land in completely different regions of the galaxy, the probability 
          of accidental collision is approximately 1 in 2²⁴⁸, for practical purposes, impossible.
        </p>

        <pre style={styles.pre}>
          <code style={styles.preCode}>keccak256(keccak256(id) - 1) = 0x3608...bbc</code>
        </pre>

        <p style={styles.p}>To see how two different namespaces land in completely different corners of the galaxy:</p>
        
        <div style={styles.visualization}>
          <div style={styles.vizHeader}>
            <div style={styles.vizTitle}>🚀 Visualization 4: Sequential vs Namespace Storage</div>
            <div style={styles.vizDescription}>Watch Solidity grow right while EIP-7201 namespaces land left — they never collide</div>
          </div>
          <iframe
            ref={viz4Ref}
            className="evm-viz-frame evm-viz-frame--tall"
            style={styles.vizFrameTall}
            src="/visualizations/viz_4_the_evm_galaxy_sequential_vs_namespace.html"
            title="Sequential vs Namespace Storage Visualization"
          />
          <div style={styles.vizControls}>
            <button style={styles.vizBtn} onClick={() => reloadViz(viz4Ref)}>↻ Replay Animation</button>
            <button style={styles.vizBtn} onClick={() => openFullscreen(viz4Ref)}>⛶ Fullscreen</button>
          </div>
        </div>

        <p style={styles.p}>
          <strong style={styles.strong}>Step 4: <code style={styles.codePurple}>& ~0xff</code> Masking to Reserve Space:</strong>
        </p>
        <p style={styles.p}>
          First is to explain what <code style={styles.codeNum}>0xff</code> means, this is an 8 bits that represents 
          the value 255 in uint. Will output the below:
        </p>
        <pre style={styles.pre}>
          <code style={styles.preCode}>
            0xff = 0x00000000000000000000000000000000000000000000000000000000000000ff{'\n'}
                    (255 in the last byte, rest are zeros)
          </code>
        </pre>
        <p style={styles.p}>
          You don't have to be a pro to understand <code style={styles.codeOp}>~</code>, just think of it as a 
          cosmic eraser that flips bits. Anywhere you see a 1, flip it to 0. Anywhere you see a 0, flip it to 1. 
          So when we apply <code style={styles.codeOp}>~</code> to <code style={styles.codeNum}>0xff</code>, we 
          get a value where the last 8 bits are zeros and the rest are ones:
        </p>
        <pre style={styles.pre}>
          <code style={styles.preCode}>
            {' '}0xff = 0x00000000000000000000000000000000000000000000000000000000000000ff{'\n'}
            ~0xff = 0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff00{'\n'}
                   (0 → 1, 1 → 0)
          </code>
        </pre>

        <p style={styles.p}>
          You still don't have to be a pro to understand <code style={styles.codeOp}>&</code>, 
          Picture it as a bit-filter: for every bit position it keeps a 1 only when both inputs have a 1. 
          Everything else becomes 0. So when we take our hashed location with <code style={styles.codePurple}>& ~0xff</code>, 
          that filter forces the last 8 bits to 0 (because <code style={styles.codePurple}>~0xff</code> has zeros there) 
          while leaving all the higher bits unchanged.
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}><code style={{ ...styles.codeSimple, padding: '6px 12px', border: '1px solid rgba(139, 92, 246, 0.4)', borderRadius: '6px', background: 'rgba(139, 92, 246, 0.1)' }}>1 & 1 = 1</code></li>
          <li style={styles.li}><code style={{ ...styles.codeSimple, padding: '6px 12px', border: '1px solid rgba(139, 92, 246, 0.4)', borderRadius: '6px', background: 'rgba(139, 92, 246, 0.1)' }}>1 & 0 = 0</code></li>
          <li style={styles.li}><code style={{ ...styles.codeSimple, padding: '6px 12px', border: '1px solid rgba(139, 92, 246, 0.4)', borderRadius: '6px', background: 'rgba(139, 92, 246, 0.1)' }}>0 & 1 = 0</code></li>
          <li style={styles.li}><code style={{ ...styles.codeSimple, padding: '6px 12px', border: '1px solid rgba(139, 92, 246, 0.4)', borderRadius: '6px', background: 'rgba(139, 92, 246, 0.1)' }}>0 & 0 = 0</code></li>
        </ul>

        <p style={styles.p}>From the result of what we have calculated:</p>
        <pre style={styles.pre}>
          <code style={styles.preCode}>keccak256(keccak256(id) - 1) = 0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc</code>
        </pre>
        <p style={styles.p}>Applying the mask:</p>
        <pre style={styles.pre}>
          <code style={styles.preCode}>
            {'    '}0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc{'\n'}
            &{'\n'}
            {'    '}0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff00{'\n'}
            ={'\n'}
            {'    '}0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d38200
          </code>
        </pre>

        <p style={styles.p}>
          By zeroing those last 8 bits, we've created a 256-slot "namespace" – an entire orbital system reserved 
          exclusively for our contract's storage:
        </p>

        <ul style={styles.ul}>
          <li style={styles.li}><code style={styles.codeSimple}>base + 0x00</code> → namespace root</li>
          <li style={styles.li}><code style={styles.codeSimple}>base + 0x01</code> → first variable</li>
          <li style={styles.li}><code style={styles.codeSimple}>base + 0x02</code> → second variable</li>
          <li style={styles.li}>...</li>
          <li style={styles.li}><code style={styles.codeSimple}>base + 0xff</code> → 256th variable</li>
        </ul>

        <hr style={styles.hr} />

        <h3 style={styles.h3}>The Complete Picture</h3>

        <p style={styles.p}>
          So <span style={{ whiteSpace: 'nowrap' }}><code style={styles.codeKeccak}>keccak256</code>(<code style={styles.codeKeccak}>keccak256</code>(<code style={styles.codeVar}>id</code>) - 1) <code style={styles.codeOp}>&</code> <code style={styles.codeOp}>~</code><code style={styles.codeNum}>0xff</code></span> is a four-step journey through our EVM galaxy:
        </p>

        <ol style={{ ...styles.ul, listStylePosition: 'outside', paddingLeft: '28px' }}>
          <li style={styles.stepItem}><strong style={styles.strong}><code style={styles.code}>keccak256(id)</code></strong><span style={styles.stepDesc}> – Pick a deterministic location based on your namespace identifier</span></li>
          <li style={styles.stepItem}><strong style={styles.strong}><code style={styles.code}>- 1</code></strong><span style={styles.stepDesc}> – Shift direction away from Solidity's natural growth pattern, to prevent preimage attacks</span></li>
          <li style={styles.stepItem}><strong style={styles.strong}><code style={styles.code}>keccak256(...)</code></strong><span style={styles.stepDesc}> – Rehash to scatter randomly across the galaxy</span></li>
          <li style={styles.stepItem}><strong style={styles.strong}><code style={styles.code}>& ~0xff</code></strong><span style={styles.stepDesc}> – Clear the last 8 bits to reserve a 256-slot orbital system</span></li>
        </ol>

        <p style={styles.p}>
          By the wisdom of the creators of the EIP 7201: instead of fighting for space in the crowded center 
          where Solidity naturally places things, we teleport to a remote, randomly-chosen corner and claim an 
          entire system of 256 size space. The probability of two slots accidentally landing in the same system? 
          Approximately 1 in 2²⁴⁸ – for practical purposes, impossible.
        </p>

        
        <div style={styles.whyDoubleHash}>
          <h3 style={styles.whyDoubleHashH3}>
            <span style={{ color: '#8b5cf6' }}>◆</span> The Double Hash - Why?
          </h3>
          <pre style={{ background: 'rgba(0, 0, 0, 0.4)', padding: '20px', borderRadius: '8px', overflowX: 'auto' }}>
            <code style={{ color: '#e0e0e0', fontSize: '0.75rem', fontFamily: "'Fira Code', monospace" }}>
              <span style={{ color: '#f59e0b' }}>keccak256</span>(abi.encode(<span style={{ color: '#22c55e' }}>uint256</span>(<span style={{ color: '#f59e0b' }}>keccak256</span>(<span style={{ color: '#60a5fa' }}>"namespace"</span>)) - 1)) <span style={{ color: '#a78bfa' }}>&amp; ~0xff</span>
              {'\n'}        |                        |                     |      |
              {'\n'}        |                        |                     |      └─ Reserve 256 slots
              {'\n'}        |                        |                     └─ Avoid Solidity patterns
              {'\n'}        |                        └─ First hash of namespace ID
              {'\n'}        └─ Second hash ensures uniform distribution
            </code>
          </pre>

          <table style={styles.tableStyle}>
            <thead>
              <tr>
                <th style={{ ...styles.thStyle, color: '#e0e0e0' }}>Aspect</th>
                <th style={{ ...styles.thStyle, color: '#9ca3af' }}>Without <code style={styles.code}>&amp; ~0xff</code></th>
                <th style={{ ...styles.thStyle, color: '#a78bfa' }}>With <code style={styles.code}>&amp; ~0xff</code></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Slot ending</td>
                <td style={{ ...styles.tdStyle, color: '#9ca3af' }}>Random (0x00-0xff)</td>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Always 0x00</td>
              </tr>
              <tr>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Reserved slots</td>
                <td style={{ ...styles.tdStyle, color: '#9ca3af' }}>1 slot</td>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>256 slots</td>
              </tr>
              <tr>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Struct support</td>
                <td style={{ ...styles.tdStyle, color: '#9ca3af' }}>Risky</td>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Safe</td>
              </tr>
              <tr>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Collision risk</td>
                <td style={{ ...styles.tdStyle, color: '#9ca3af' }}>Higher</td>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Minimal</td>
              </tr>
              <tr>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Use case</td>
                <td style={{ ...styles.tdStyle, color: '#9ca3af' }}>Single values</td>
                <td style={{ ...styles.tdStyle, color: '#d1d5db' }}>Structured data</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <hr style={styles.hr} />

      {/* Practical Implications */}
      <section id="implications">
        <p style={styles.p}>The EVM galaxy suddenly becomes a lot less chaotic, and a lot more navigable.</p>
      </section>
      </div>
    </div>
  );
};

export default EVMGalaxy;
